// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import loadWarehouses from "@insite/client-framework/Store/Components/FindLocationModal/Handlers/LoadWarehouses";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getWarehousesDataView } from "@insite/client-framework/Store/Data/Warehouses/WarehousesSelectors";
import setRequestedPickUpDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedPickUpDate";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import { FindLocationModalStyles } from "@insite/content-library/Components/FindLocationModal";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import updateXCheckoutState from "@pace/App/Handlers/Checkout/UpdateLoacalState";
import xUseTimeSlots, { getDateTime, setDateTime } from "@pace/App/Hooks/xUseTimeSlots";
import { CustomAppState } from "@pace/App/Store/Reducers";
import DatePicker, { DatePickerState } from "@pace/MobiusOverrides/DatePicker";
import DynamicDropdown, { OptionObject } from "@pace/MobiusOverrides/DynamicDropdown/DynamicDropdown";
import Select from "@pace/MobiusOverrides/Select";
import IconClock from "@pace/XDL/Icons/IconClock";
import { parseBoolean } from "@pace/XDL/Utilities";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// <XNG-Changes>
const mapStateToProps = (state: CustomAppState) => {
    const settingsCollection = getSettingsCollection(state);
    const { session } = state.context;
    const { cartId } = state.pages.checkoutShipping;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);

    const warehousesDataView = getWarehousesDataView(state, state.components.findLocationModal.getWarehousesParameter);
    const warehousesHashMap = warehousesDataView.value?.reduce<Dictionary<WarehouseModel>>((acc, curr) => {
        acc[curr.id] = curr;
        return acc;
    }, {});
    const dropDownOptions: OptionObject[] =
        warehousesDataView.value?.map(warehouse => ({
            optionText: `${warehouse.name} - ${warehouse.description} - ${warehouse.address1} ${warehouse.address2} ${warehouse.city} ${warehouse.state} ${warehouse.postalCode} ${warehouse.phone}`,
            optionValue: warehouse.id,
        })) || [];

    return {
        cart: cartState.value,
        showPickUpDate:
            settingsCollection.accountSettings.enableWarehousePickup &&
            settingsCollection.cartSettings.enableRequestPickUpDate &&
            session.fulfillmentMethod === FulfillmentMethod.PickUp,
        maximumDeliveryPeriod: settingsCollection.cartSettings.maximumDeliveryPeriod,
        // XNG-Changes
        warehousesHashMap: warehousesHashMap || {},
        dropDownOptions: dropDownOptions || {},
        shippingPageState: (state as CustomAppState).xCheckout.shipping,
        requestedPickupDate: state.pages.checkoutReviewAndSubmit.requestedPickupDate,
    };
};

const mapDispatchToProps = {
    loadWarehouses,
    setRequestedPickUpDate,
    updateXCheckoutState,
};
// </XNG-Changes>

export interface PickUpAddressStyles {
    // container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    findLocationLink?: LinkPresentationProps;
    selectedLocationGridItem?: GridItemProps;
    selectedLocationName?: TypographyPresentationProps;
    selectedLocationAddressDisplay?: AddressInfoDisplayStyles;
    findLocationModal?: FindLocationModalStyles;
    // XNG-Changes
    container?: InjectableCss;
    sectionContainer?: InjectableCss;
    dateTimeWrapper?: InjectableCss;
}

export const pickUpAddressStyles: PickUpAddressStyles = {
    // container: { gap: 20 },
    headingGridItem: {
        width: 12,
        css: css`
            align-items: center;
        `,
    },
    headingText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    findLocationLink: {
        css: css`
            margin-left: 20px;
        `,
    },
    selectedLocationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
};

const PickUpAddress: FC<
    {
        address?: WarehouseModel;
        onChange?: (address: WarehouseModel) => void;
        extendedStyles?: PickUpAddressStyles;
    } & ReturnType<typeof mapStateToProps> &
        ResolveThunks<typeof mapDispatchToProps>
> = ({
    address,
    onChange,
    extendedStyles,
    loadWarehouses,
    setRequestedPickUpDate,
    updateXCheckoutState,
    // XNG-Changes
    cart,
    showPickUpDate,
    maximumDeliveryPeriod,
    warehousesHashMap,
    dropDownOptions,
    shippingPageState,
    requestedPickupDate,
}) => {
    const [styles] = React.useState(() => mergeToNew(pickUpAddressStyles, extendedStyles));
    const [isFindLocationOpen, setIsFindLocationOpen] = React.useState(false);
    const handleOpenFindLocation = () => {
        setIsFindLocationOpen(true);
    };
    const handleFindLocationModalClose = () => setIsFindLocationOpen(false);
    const handleWarehouseSelected = (warehouse: WarehouseModel) => {
        if (onChange) {
            onChange(warehouse);
            setIsFindLocationOpen(false);
        }
    };

    // <XNG-Changes>
    const [search, setSearch] = React.useState("");
    // XNG-Chagnes: here set requested delvery Date and Time
    useEffect(() => {
        if (cart) {
            if (cart.requestedPickupDate) {
                const requestedPickUpDate = new Date(cart.requestedPickupDate);
                setRequestedPickUpDate({ requestedPickUpDate });
                if (parseBoolean(cart.properties.deliverAnytime)) {
                    updateTimeslot("");
                } else {
                    updateTimeslot(getDateTime(requestedPickUpDate));
                }
            }
        }
    }, [cart]);

    if (!cart) {
        return null;
    }

    const handleRequestPickUpDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        if (shippingPageState.timeslot !== "" && selectedDay) {
            setDateTime(selectedDay, shippingPageState.timeslot);
        }
        setRequestedPickUpDate({
            requestedPickUpDate: selectedDay,
            requestedPickUpDateDisabled: selectedDayDisabled,
        });
    };

    const updateTimeslot = (timeslot: string) => {
        updateXCheckoutState({ shipping: { timeslot } });
        if (timeslot !== "" && requestedPickupDate) {
            setDateTime(requestedPickupDate, timeslot);
            setRequestedPickUpDate({
                requestedPickUpDate: requestedPickupDate,
                requestedPickUpDateDisabled: false,
            });
        }
    };
    // Manage Loading Warehouses on State Changes
    React.useEffect(() => {
        loadWarehouses({
            radius: 999999,
            onlyPickupWarehouses: true,
            pageSize: 40,
            search,
            page: 1,
            latitude: 0,
            longitude: 0,
        });
    }, [search]);

    const { timeSlots } = xUseTimeSlots({
        date: requestedPickupDate,
    });

    const onInputChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.currentTarget.value);
    };

    const minDate = new Date();
    minDate.setHours(0, 0, 0, 0);
    // </XNG-Changes>
    return (
        <StyledWrapper {...styles.container} data-test-selector="checkoutShipping_pickUpAddress">
            <StyledWrapper {...styles.sectionContainer}>
                <Typography {...styles.headingText} forwardAs="h2">
                    {translate("Will Call Info")}
                </Typography>
                {/* <Link
                    {...styles.findLocationLink}
                    type="button"
                    onClick={handleOpenFindLocation}
                    data-test-selector="checkoutShipping_pickUpAddress_findLocation"
                >
                    {translate("Find Location")}
                </Link> */}
                {/** XNG-Changes: add Branch Dropdown */}
                <DynamicDropdown
                    label={translate("Branch")}
                    options={dropDownOptions}
                    selected={address?.id}
                    onInputChange={onInputChanged}
                    onSelectionChange={warehouseId => {
                        if (!warehouseId || !warehousesHashMap[warehouseId]) {
                            return;
                        }
                        onChange?.(warehousesHashMap[warehouseId]);
                    }}
                />
            </StyledWrapper>
            {/* {address && (
                <GridItem {...styles.selectedLocationGridItem}>
                    <Typography
                        {...styles.selectedLocationName}
                        data-test-selector="checkoutShipping_pickUpAddress_warehouseName"
                    >
                        {address.description || address.name}
                    </Typography>
                    <AddressInfoDisplay {...address} extendedStyles={styles.selectedLocationAddressDisplay} />
                </GridItem>
            )} */}
            {/** XNG-Changes */}
            {showPickUpDate && (
                <StyledWrapper {...styles.sectionContainer} data-test-selector="checkoutPickUpRequestedDeliveryDate">
                    <Typography {...styles.headingText} forwardAs="h2">
                        {translate("Requested Pickup Date / Time")}
                    </Typography>
                    <StyledWrapper {...styles.dateTimeWrapper}>
                        <DatePicker
                            label={`${translate("Date")}`}
                            // hint={siteMessage("Checkout_RequestedPickupDateInformation")}
                            // XNG-Changes
                            selectedDay={requestedPickupDate!}
                            dateTimePickerProps={{
                                // XNG-Changes: set min date start from 00:00 to avoid validation
                                minDate,
                                maxDate: addDays(new Date(), maximumDeliveryPeriod),
                            }}
                            onDayChange={handleRequestPickUpDateChanged}
                            monthAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                "Enter month, format M M",
                            )}`}
                            dayAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                "Enter day, format D D",
                            )}`}
                            yearAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                "Enter year, format Y Y Y Y",
                            )}`}
                        />
                        {/** <XNG-Changes> */}
                        <Select
                            label={translate("Time")}
                            placeholder={translate("Anytime")}
                            value={shippingPageState.timeslot}
                            onChange={event => updateTimeslot(event.target.value)}
                            leftIconProps={{
                                src: IconClock,
                            }}
                            disabled={!requestedPickupDate}
                        >
                            <option value="">{translate("Anytime")}</option>
                            {timeSlots.map((value, index) => (
                                <option key={index} value={value}>
                                    {value}
                                </option>
                            ))}
                        </Select>
                        {/** </XNG-Changes> */}
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {/* <FindLocationModal
                modalIsOpen={isFindLocationOpen}
                onWarehouseSelected={handleWarehouseSelected}
                onModalClose={handleFindLocationModalClose}
                extendedStyles={styles.findLocationModal}
            /> */}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(PickUpAddress);
